import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, Star, Sun, Moon, Flower2, Gem, Snowflake, Heart, BookOpen, Flame, Compass, Award, Loader2, ArrowLeft } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { CoconBadge } from "@/components/CoconBadge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMBLEM_MAP = { sparkles: Sparkles, star: Star, sun: Sun, moon: Moon, flower: Flower2, diamond: Gem, snowflake: Snowflake, heart: Heart };
const BADGE_ICONS = { star1: Star, star5: Sparkles, star15: Compass, epi1: BookOpen, epi10: BookOpen, streak3: Flame, streak7: Flame, days30: Award };

export default function ExplorerPublic() {
  const { handle } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ex, setEx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [sparkBusy, setSparkBusy] = useState(false);
  const [sparked, setSparked] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setLoading(true);
    api.get(`/explorer/${handle}`)
      .then(({ data }) => { setEx(data); setNotFound(false); setSparked(!!data.is_sparked); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [handle]);

  const sendSpark = async () => {
    if (!user) { navigate("/entrer"); return; }
    if (sparkBusy || sparked) return;
    setSparkBusy(true);
    try {
      const { data } = await api.post(`/explorer/${handle}/spark`);
      setEx((e) => ({ ...e, sparks: data.sparks }));
      setSparked(true);
      toast.success(t("explorer.sparkSent"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("explorer.error")); }
    finally { setSparkBusy(false); }
  };

  const toggleFollow = async () => {
    if (!user) { navigate("/entrer"); return; }
    if (followBusy) return;
    setFollowBusy(true);
    try {
      if (ex.is_following) { await api.delete(`/explorer/${handle}/follow`); setEx((e) => ({ ...e, is_following: false })); }
      else { await api.post(`/explorer/${handle}/follow`); setEx((e) => ({ ...e, is_following: true })); toast.success(t("explorer.nowFollowing", { name: ex.name })); }
    } catch { toast.error(t("explorer.error")); }
    finally { setFollowBusy(false); }
  };

  const Emblem = ex ? (EMBLEM_MAP[ex.emblem] || Sparkles) : Sparkles;

  return (
    <div className="min-h-screen bg-background text-foreground starfield">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight" data-testid="explorer-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/entrer" data-testid="explorer-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-2xl mx-auto px-6 pt-32 pb-24">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={30} /></div>
        ) : notFound || !ex ? (
          <div className="text-center py-24" data-testid="explorer-notfound">
            <p className="font-display italic text-2xl text-foreground/60 mb-6">{t("explorer.notFound")}</p>
            <Link to="/" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold"><ArrowLeft size={16} /> {t("explorer.home")}</Link>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="explorer-card">
            <p className="text-center uppercase tracking-[0.28em] text-[0.7rem] text-primary/75 mb-8">{t("explorer.eyebrow")}</p>

            <div className="glass frame-pulse rounded-3xl p-8 sm:p-10 text-center">
              <div className="flex justify-center mb-5">
              <span className={ex.cocon ? "member-cocon-ring rounded-full inline-flex" : "inline-flex"} title={ex.cocon ? "Membre Le Cocoon" : undefined} data-testid="explorer-cocon-ring">
              <div className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center bg-primary/12 border border-primary/30"
                style={{ boxShadow: "0 0 32px hsl(32 90% 55% / 0.28)" }}>
                {ex.avatar ? <img src={`${BACKEND_URL}/api/avatar/${ex.id}`} alt="" className="w-full h-full object-cover" data-testid="explorer-avatar" />
                  : <Emblem size={42} className="text-primary" strokeWidth={1.5} />}
              </div>
              </span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange mb-2 flex items-center justify-center gap-2 flex-wrap" data-testid="explorer-name">{ex.name}{ex.cocon && <CoconBadge size="lg" />}</h1>
              {ex.motto ? (
                <p className="font-display italic text-foreground/70 text-lg mb-3" data-testid="explorer-motto">« {ex.motto} »</p>
              ) : null}
              {ex.member_since && (
                <p className="text-xs text-foreground/45 mb-6">{t("explorer.memberSince")} {ex.member_since}{typeof ex.days_since === "number" && <> · <span className="text-primary/70">{t("explorer.wonderDays", { count: ex.days_since })}</span></>}</p>
              )}

              {/* sparks + actions */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-2">
                {!ex.is_self && (
                  <button onClick={sendSpark} disabled={sparkBusy || sparked} data-testid="explorer-spark-btn"
                    className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold disabled:opacity-70">
                    {sparkBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {sparked ? t("explorer.sparkedBtn") : t("explorer.sparkBtn")}
                  </button>
                )}
                {!ex.is_self && (
                  <button onClick={toggleFollow} disabled={followBusy} data-testid="explorer-follow-btn"
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border transition-colors ${ex.is_following ? "border-primary/40 text-primary bg-primary/10" : "border-white/12 text-foreground/70 hover:text-primary hover:border-primary/40"}`}>
                    {followBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                    {ex.is_following ? t("explorer.followingBtn") : t("explorer.followBtn")}
                  </button>
                )}
              </div>
              <p className="text-sm text-primary/80 inline-flex items-center gap-1.5" data-testid="explorer-sparks">
                <Sparkles size={14} /> {t("explorer.sparks", { count: ex.sparks })}
              </p>
            </div>

            {/* stats */}
            <div className="grid grid-cols-4 gap-3 mt-5" data-testid="explorer-stats">
              {[["stars", ex.stats.stars], ["epiphanies", ex.stats.epiphanies], ["streak", ex.stats.streak], ["activeDays", ex.stats.active_days]].map(([k, v]) => (
                <div key={k} className="glass rounded-2xl py-4 text-center">
                  <p className="font-display text-2xl sm:text-3xl text-gradient-orange tabular-nums">{v}</p>
                  <p className="text-[10px] uppercase tracking-wide text-foreground/45 mt-1">{t(`explorer.stat.${k}`)}</p>
                </div>
              ))}
            </div>

            {/* domains */}
            {ex.domains?.length > 0 && (
              <div className="mt-5 glass rounded-2xl p-6" data-testid="explorer-domains">
                <p className="uppercase tracking-[0.2em] text-[0.65rem] text-primary/70 mb-4">{t("explorer.domainsTitle")}</p>
                <div className="flex flex-wrap gap-2">
                  {ex.domains.map((d) => (
                    <span key={d} className="text-sm px-3 py-1.5 rounded-full border border-primary/20 bg-primary/[0.05] text-foreground/75">{d}</span>
                  ))}
                </div>
              </div>
            )}

            {/* badges */}
            {ex.badges?.length > 0 && (
              <div className="mt-5 glass rounded-2xl p-6" data-testid="explorer-badges">
                <p className="uppercase tracking-[0.2em] text-[0.65rem] text-primary/70 mb-4">{t("explorer.badgesTitle")}</p>
                <div className="flex flex-wrap gap-3">
                  {ex.badges.map((b) => {
                    const Icon = BADGE_ICONS[b] || Award;
                    return (
                      <span key={b} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full bg-primary/[0.08] border border-primary/25 text-foreground/85" data-testid={`explorer-badge-${b}`}>
                        <Icon size={15} className="text-primary" /> {t(`atelier.profile.badges.${b}.name`)}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-10 text-center">
              <Link to="/" className="text-sm text-foreground/45 hover:text-primary transition-colors inline-flex items-center gap-1.5">
                <Compass size={14} /> {t("explorer.discover")}
              </Link>
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
}
